import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { atsPickBest } from '../../lib/api';
import type { AtsCompareRun, AtsRecord } from '../../lib/api';
import ResumePaper from './ResumePaper';
import { Btn, C, MODEL_META, Modal, ScoreRing, Spinner, fmtTimer, modelName, rupees } from './ui';

// A model comparison: each model's version of the same resume side by side —
// the ATS score it reached, the lines the fact check had to stop, the time
// and the cost — and 🏆 to keep the best one.

// The best value of each measure; null where every version is the same
type Best = { score: number | null; held: number | null; sec: number | null; inr: number | null };
const bestOf = (vals: number[], low: boolean) => (new Set(vals).size > 1 ? (low ? Math.min(...vals) : Math.max(...vals)) : null);

const Stat: React.FC<{ label: string; value: React.ReactNode; best?: string }> = ({ label, value, best }) => (
  <div className="flex items-center justify-between gap-2 py-2 border-b border-gray-100 last:border-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm font-extrabold text-gray-900 flex items-center gap-1.5">
      {best && <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">{best}</span>}
      {value}
    </span>
  </div>
);

const RunCard: React.FC<{ run: AtsCompareRun; best: Best | null; winner: boolean; picking: boolean; now: number; onRead: () => void; onPick: () => void }> = ({ run, best, winner, picking, now, onRead, onPick }) => {
  const meta = MODEL_META[run.model];
  const secs = run.busySince ? Math.max(0, Math.round((now - Date.parse(run.busySince)) / 1000)) : 0;
  return (
    <div className={`relative bg-white rounded-3xl border-2 ats-card ats-fade flex flex-col ${winner ? '' : 'border-gray-100'}`}
      style={winner ? { borderColor: C.gold, boxShadow: '0 18px 40px -18px rgba(240,180,41,.65)' } : {}} data-run={run.model}>
      {winner && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-black px-3 py-1 rounded-full shadow" style={{ background: C.gold, color: C.purple }}>🏆 Picked</span>}
      <div className="p-5 pb-3 rounded-t-3xl" style={{ background: `linear-gradient(180deg, ${meta?.color ?? '#6b7280'}12, transparent)` }}>
        <p className="font-black text-base" style={{ color: meta?.color }}>{meta?.icon} {modelName(run.model)}</p>
      </div>
      <div className="px-5 pb-5 flex-1 flex flex-col">
        {run.busy ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-8 gap-3">
            <Spinner size={30} />
            <p className="text-sm font-extrabold text-gray-800">Writing its version…</p>
            <p className="text-xs text-gray-400">{fmtTimer(secs)}{run.model === 'claude-fable-5-1' ? ' · Fable takes longer' : ''}</p>
          </div>
        ) : !run.done ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-8 gap-2">
            <span className="text-3xl">⚠️</span>
            <p className="text-sm font-extrabold text-red-700">This one failed</p>
            <p className="text-xs text-gray-500">{run.error || 'Something went wrong.'}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 py-2">
              <ScoreRing value={run.scoreAfter} size={84} stroke={8} />
              <div>
                <p className="text-xs text-gray-500">ATS score</p>
                <p className="text-sm font-extrabold text-green-700">+{run.gain}% from the original</p>
                {best && run.scoreAfter === best.score && <p className="text-[11px] font-extrabold text-amber-700 mt-0.5">🥇 Highest score</p>}
              </div>
            </div>
            <div className="mt-1">
              <Stat label="Lines the fact check stopped" value={run.held === 0 ? <span className="text-green-700">0 ✓</span> : <span className="text-amber-700">{run.held}</span>}
                best={best && run.held === best.held ? 'Most careful' : undefined} />
              <Stat label="Time" value={`${Math.round(run.sec)}s`} best={best && run.sec === best.sec ? 'Fastest' : undefined} />
              <Stat label="Cost" value={rupees(run.inr)} best={best && run.inr === best.inr ? 'Cheapest' : undefined} />
              <Stat label="Suggestions" value={run.boosters.length} />
            </div>
            <div className="flex gap-2 mt-4">
              <Btn kind="ghost" className="flex-1" onClick={onRead}>👁 Read it</Btn>
              <Btn kind={winner ? 'soft' : 'gold'} className="flex-1" disabled={picking || winner} onClick={onPick}>{winner ? '✓ In use' : '🏆 Use this one'}</Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ComparePanel: React.FC<{ rec: AtsRecord; pw: string; onChanged: (r?: AtsRecord) => void; onBack: () => void; backLabel: string }> = ({ rec, pw, onChanged, onBack, backLabel }) => {
  const cmp = rec.compare!;
  const [now, setNow] = useState(Date.now());
  const [reading, setReading] = useState<AtsCompareRun | null>(null);
  const [showChanges, setShowChanges] = useState(true);
  const [picking, setPicking] = useState(false);
  const running = cmp.runs.some(r => r.busy);
  const done = cmp.runs.filter(r => r.done && r.resume);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);

  const best: Best | null = done.length > 1 ? {
    score: bestOf(done.map(r => r.scoreAfter), false),
    held: bestOf(done.map(r => r.held), true),
    sec: bestOf(done.map(r => r.sec), true),
    inr: bestOf(done.map(r => r.inr), true),
  } : null;

  const pick = async (run: AtsCompareRun) => {
    if (rec.step === 'done' && !window.confirm(`Use ${modelName(run.model)}'s version? It replaces the current resume, including your edits. The chat stays.`)) return;
    setPicking(true);
    try {
      const res = await atsPickBest(pw, rec._id, run.id);
      setReading(null);
      onChanged(res.resume);
      toast.success(`🏆 Using ${modelName(run.model)}'s version`);
    } catch (err: any) { toast.error(err.message); }
    finally { setPicking(false); }
  };

  return (
    <div className="space-y-5 ats-fade">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <button onClick={onBack} className="text-xs font-bold text-purple-700 hover:underline mb-2">← {backLabel}</button>
          <p className="text-xl font-black text-gray-900">⚖ Model comparison</p>
          <p className="text-sm text-gray-500 mt-0.5">
            Same check, same job — each model wrote its own version.{' '}
            {running ? 'They are writing now; you can leave this page.' : cmp.winner ? `You picked ${modelName(cmp.winner)}.` : 'Read them and keep the best one.'}
          </p>
        </div>
        {running && <span className="inline-flex items-center gap-2 text-xs font-bold text-purple-800 bg-purple-50 px-3 py-2 rounded-xl"><Spinner size={14} /> {cmp.runs.filter(r => r.busy).length} still writing</span>}
      </div>

      <div className={`grid gap-4 pt-2 ${cmp.runs.length >= 4 ? 'md:grid-cols-2 xl:grid-cols-4' : cmp.runs.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {cmp.runs.map(run => (
          <RunCard key={run.id} run={run} best={best} winner={cmp.chosenId === run.id} picking={picking} now={now}
            onRead={() => setReading(run)} onPick={() => pick(run)} />
        ))}
      </div>
      <p className="text-[11px] text-gray-400">Every version keeps the facts and the education of the original resume. "Lines the fact check stopped" counts where a model tried to add something that isn't true — fewer is better. Results go to the Model Lab.</p>

      <Modal open={!!reading} onClose={() => setReading(null)} wide
        title={reading ? <span style={{ color: MODEL_META[reading.model]?.color }}>{MODEL_META[reading.model]?.icon} {modelName(reading.model)}'s version</span> : ''}
        sub={reading ? `ATS score ${reading.scoreAfter}% · ${reading.held} line${reading.held === 1 ? '' : 's'} stopped by the fact check · ${rupees(reading.inr)}` : ''}
        footer={reading && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer flex-1">
              <input type="checkbox" checked={showChanges} onChange={e => setShowChanges(e.target.checked)} className="accent-purple-700" /> Show the AI's changes
            </label>
            <Btn kind="gold" size="lg" disabled={picking || cmp.chosenId === reading.id} onClick={() => pick(reading)}>{cmp.chosenId === reading.id ? '✓ In use' : '🏆 Use this one'}</Btn>
          </div>
        )}>
        {reading?.resume && (
          <div className="space-y-4">
            <div className="bg-[#f4f2f8] rounded-2xl p-3 sm:p-5">
              <ResumePaper doc={reading.resume} design={rec.design} showChanges={showChanges} readOnly />
            </div>
            {reading.fixes.length > 0 && (
              <div className="rounded-2xl p-4 bg-orange-50">
                <p className="text-sm font-extrabold text-gray-900 mb-1">🛡️ What the fact check stopped</p>
                <ul className="space-y-1">{reading.fixes.map((f, i) => <li key={i} className="text-xs text-orange-950">• {f}</li>)}</ul>
              </div>
            )}
            {reading.hrMessage && (
              <div className="rounded-2xl p-4 bg-gray-50">
                <p className="text-sm font-extrabold text-gray-900 mb-1">✉️ Its HR message</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{reading.hrMessage}</p>
              </div>
            )}
            {reading.boosters.length > 0 && (
              <div className="rounded-2xl p-4 bg-purple-50/60">
                <p className="text-sm font-extrabold text-gray-900 mb-1">💡 Its suggestions</p>
                <ul className="space-y-1">{reading.boosters.map((b, i) => <li key={i} className="text-sm text-gray-700">• {b}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ComparePanel;
